import { ChevronRight } from "@mui/icons-material";
import { Box, Button, Grid2, Paper, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { GqlDashboardFieldworkerNextDays } from "~/__generated__/index.ts";
import { parseStringToDate } from "~/modules/app/util/dateHelpers.ts";

import AvailabilitySection from "./AvailabilitySection.tsx";
import DateSection from "./DateSection.tsx";

const dayFormatter = Intl.DateTimeFormat(undefined, {
    weekday: "narrow",
});

type Props = {
    component: GqlDashboardFieldworkerNextDays;
};

export default function DashboardFieldworkerNextDays({ component }: Props) {
    const { t } = useTranslation("app");
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const days = isMobile ? component.days.slice(0, 3) : component.days;
    const numberOfDaysToDisplay = days.length;

    return (
        <Grid2 size={12}>
            <Box paddingBottom={2}>
                <Typography variant="h3">{t("dashboardComponents.dashboardFieldworkerNextDays.title")}:</Typography>
                <Typography variant="body1">
                    {t("dashboardComponents.dashboardFieldworkerNextDays.description", {
                        numberOfDays: numberOfDaysToDisplay,
                    })}
                </Typography>
            </Box>
            <Paper variant="outlined" sx={{ padding: 2 }}>
                <Stack display="flex" direction="row" justifyContent="space-between" marginBottom={2}>
                    <Typography variant="h4">{t("dashboardComponents.dashboardFieldworkerNextDays.availability")}</Typography>
                    <Button
                        endIcon={<ChevronRight />}
                        variant="text"
                        component={Link}
                        to={"fieldworker/availability"}
                        size="small"
                    >
                        {t("dashboardComponents.dashboardFieldworkerNextDays.updateAvailability")}
                    </Button>
                </Stack>
                <Grid2
                    container
                    textAlign="center"
                    columns={numberOfDaysToDisplay}
                    sx={{
                        "--Grid-borderWidth": "1px",
                        borderColor: "divider",
                        "& > div": {
                            borderRight: "var(--Grid-borderWidth) solid",
                            borderBottom: "var(--Grid-borderWidth) solid",
                            borderColor: "divider",
                            [`&:nth-of-type(${numberOfDaysToDisplay}n)`]: {
                                borderRight: 0,
                            },
                            [`&:nth-last-of-type(-n + ${numberOfDaysToDisplay})`]: {
                                borderBottom: 0,
                            },
                        },
                    }}
                >
                    {days.map(day => (
                        <Grid2 size={1} key={day.date}>
                            <Typography variant="h6" flex={1} textAlign="center">
                                {dayFormatter.format(new Date(day.date))}
                            </Typography>
                        </Grid2>
                    ))}
                    {days.map(day => {
                        return <DateSection key={day.date} day={parseStringToDate(day.date)} />;
                    })}
                    {days.map(day => {
                        return <AvailabilitySection key={day.date} availabilityData={day.availabilities} />;
                    })}
                </Grid2>
            </Paper>
        </Grid2>
    );
}
